import { useState, useEffect } from 'react';
import { cachedFetchJSON } from '../utils/fetchCache';

const CENSUS_BASE = 'https://api.census.gov/data';
const API_KEY = process.env.REACT_APP_CENSUS_API_KEY;
const GEO_TTL = 7 * 24 * 60 * 60 * 1000;

/**
 * Fetches all US counties from the Census ACS 5-year API.
 * Returns { counties: [{ state, county, fips5, name, stateName, fullName }], loading, error }
 * Sorted alphabetically by fullName. Cached with 7-day TTL.
 */
export function useAllCounties() {
  const [counties, setCounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams({
      get: 'NAME',
      for: 'county:*',
      ...(API_KEY ? { key: API_KEY } : {})
    });
    const url = `${CENSUS_BASE}/2023/acs/acs5?${params}`;

    cachedFetchJSON(url, GEO_TTL)
      .then(rows => {
        if (!Array.isArray(rows) || rows.length < 2) {
          setCounties([]);
          setLoading(false);
          return;
        }
        const headers = rows[0];
        const stateIdx = headers.indexOf('state');
        const countyIdx = headers.indexOf('county');
        const nameIdx = headers.indexOf('NAME');

        const result = rows.slice(1).map(row => {
          const st = row[stateIdx];
          const co = row[countyIdx];
          const fullName = row[nameIdx]; // e.g. "Routt County, Colorado"
          const commaIdx = fullName.lastIndexOf(',');
          const name = commaIdx > 0 ? fullName.slice(0, commaIdx).trim() : fullName;
          const stateName = commaIdx > 0 ? fullName.slice(commaIdx + 1).trim() : '';
          return { state: st, county: co, fips5: st + co, name, stateName, fullName };
        }).sort((a, b) => a.fullName.localeCompare(b.fullName));

        setCounties(result);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  return { counties, loading, error };
}
